import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { MenuItemBase } from '#pplx/frontend/aether/components/internal/menu/MenuItemBase.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
/**
 * A menu item that acts as a trigger for a submenu.
 * Automatically includes a chevron icon as a trailing accessory.
 *
 * Should be used as the triggerElement prop of Menu.Submenu.
 */
export function MenuSubmenuItem({ children, disabled, leadingAccessory, subtitle, isExpanded, checked, trailingValue, trailingAccessory, onTriggerSelect, ...rest }) {
    const { isMobileStyle } = useAetherContext();
    const content = trailingValue == null ? (children) : (_jsxs("span", { className: "grid min-w-0 grid-cols-[max-content_minmax(0,1fr)] items-center gap-1", children: [_jsx("span", { children: children }), _jsx("span", { className: "min-w-0 max-w-full justify-self-end truncate text-secondary", children: trailingValue })] }));
    // Mobile renders within a dialog (i.e: bottom sheet) so
    // we render as a button role for proper keyboard navigation.
    // Desktop mode will be wrapped by Radix's SubTrigger which provides proper accessibility.
    if (isMobileStyle) {
        return (_jsx(MenuItemBase, { leadingAccessory: leadingAccessory, trailingAccessory: _jsx("div", { className: classNames('flex items-center transition-transform', {
                    'rotate-90': isExpanded,
                }), children: _jsx(Icon, { icon: $icon('chevron-right'), size: "small", color: "light" }) }), subtitle: subtitle, role: "button", tabIndex: disabled ? -1 : 0, "aria-disabled": disabled, "aria-expanded": isExpanded, ...rest, children: content }));
    }
    // Default to the chevron that signals a nested submenu; an explicit value
    // (including `null` for nothing) customizes that trailing affordance.
    const desktopTrailingAccessory = trailingAccessory === undefined ? (_jsx(Icon, { icon: $icon('chevron-right'), size: "small", color: "light" })) : ((trailingAccessory ?? undefined));
    // Radix's SubTrigger injects its own onClick (to open the submenu) through
    // `rest`. Compose it with `onTriggerSelect` so activating the row both opens
    // the submenu and runs the consumer's action, rather than one clobbering the
    // other when `rest` is spread.
    const { onClick: radixOnClick, onKeyDown: radixOnKeyDown, 'aria-disabled': radixAriaDisabled, ...restProps } = rest;
    const isDisabled = disabled === true || radixAriaDisabled === true || radixAriaDisabled === 'true';
    const handleClick = (event) => {
        radixOnClick?.(event);
        if (!isDisabled) {
            onTriggerSelect?.();
        }
    };
    const handleKeyDown = (event) => {
        radixOnKeyDown?.(event);
        if (!isDisabled && !event.repeat && (event.key === 'Enter' || event.key === ' ')) {
            onTriggerSelect?.();
        }
    };
    return (_jsx(MenuItemBase, { leadingAccessory: leadingAccessory, trailingAccessory: desktopTrailingAccessory, subtitle: subtitle, onClick: handleClick, onKeyDown: handleKeyDown, ...restProps, ...(checked !== undefined
            ? {
                role: 'menuitemradio',
                'aria-checked': checked,
            }
            : undefined), "aria-disabled": isDisabled || undefined, children: content }));
}
//# sourceMappingURL=MenuSubmenuItem.js.map